import { useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import useClickOutside from './useClickOutside';
import useEscape from './useEscape';
import useTrapFocus from './useTrapFocus';
/**
 * Hook to allow you to implement your own Dialog component.
 * This takes care of focus, and closing the Dialog by
 * click outside it, or hitting Escape.
 */
export default function useDialogHelpers(options) {
  var _options$id = options.id,
    id = _options$id === void 0 ? uuidv4() : _options$id,
    wrapperElement = options.wrapperElement,
    closeFunction = options.closeFunction,
    isOpen = options.isOpen,
    disableClosers = options.disableClosers;

  /**
   * Allows you to disable these two hooks.
   */
  if (!disableClosers) {
    /**
     * Close the Dialog by pressing Escape.
     */
    useClickOutside({
      refs: [wrapperElement],
      onClick: closeFunction
    });

    /**
     * Close the Dialog by pressing Escape.
     */
    useEscape({
      onPress: closeFunction
    });
  }

  /**
   * Make sure focus does not leave the Dialog while it's open.
   */
  useTrapFocus({
    ref: wrapperElement,
    isActive: isOpen
  });

  /**
   * Sets focus on the popup so that screen readers announce
   * that the user is currently on a dialog.
   */
  useEffect(function () {
    if (isOpen && wrapperElement.current) {
      wrapperElement.current.setAttribute('tabindex', '-1');
      wrapperElement.current.focus();
    }
    function removeFocusProperties() {
      if (wrapperElement.current) {
        wrapperElement.current.removeAttribute('tabindex');
      }
    }
    if (wrapperElement.current) {
      wrapperElement.current.addEventListener('blur', removeFocusProperties);
    }
    return function () {
      if (wrapperElement.current) {
        wrapperElement.current.removeEventListener('blur', removeFocusProperties);
      }
    };
  }, [isOpen]);

  /**
   * Create a labelId for A11Y
   */
  var labelId = "".concat(id, "-label");
  var dialogHelpersProps = {
    'aria-role': 'dialog',
    'aria-labelledby': labelId
  };
  return {
    id: id,
    labelId: labelId,
    dialogHelpersProps: dialogHelpersProps
  };
}